import { BEAST_HOME } from '../src/rules.ts';
import { RECIPES, ITEMS } from '../src/sim/inventory.ts';
/* What the manual says, as data.
 *
 * Kept apart from the page so a check can read it without a DOM, and so the
 * one thing a manual must not do — go quietly out of date — is a test failure
 * rather than a discovery. Every list here is cross-checked in test/soak.mjs
 * against the game's OWN tables: add an enemy, a tool, a tile or a shop item
 * and forget the manual, and the suite says so.
 */

export const STORY = [
  'Three great beasts roam the wilds: the sow, the old shell and the bramble '
  + 'mother. While they walk, the wilds keep their kin awake: the things in the '
  + 'grass are hungry and confused, and nothing you fell stays fallen for long.',
  'Someone cut the standing stones when the beasts came, and cut the beasts\' '
  + 'places into them, bearing by bearing; they still turn toward whatever is '
  + 'left standing. Whoever they were, the stones are what came back.',
  'You are a forager. You have a sickle, a bag, a good pair of legs, and a map '
  + 'that fills as you walk. Fell the three and the wilds go quiet. Leave when you choose, '
  + 'by holding at a standing stone, and what you changed is written down: the days, '
  + 'the beasts, the things you set down, the ground you took. At night a pack comes '
  + 'hunting from a den of its own and leaves bones where it ate; a light keeps it off.',
  'A weapon wears: every landed blow spends one, and a spent blade strikes as bare hands '
  + 'would until three glim whet it. Bare hands strike for half a point and cut no grass, '
  + 'and neither a fist nor a blade worn dull takes anything off a great beast: they reel '
  + 'the lesser things, but a beast is felled with an edge or not at all. The hotbar says '
  + 'what is in hand, what it does, how far it reaches and how many blows are left in it.',
  'Nothing here will explain itself twice. The land is the tutorial: the '
  + 'standing stones name the beasts\' places and which way they lie, a spear '
  + 'left in the grass is telling you something wants reach, and a hedge that '
  + 'burns is telling you what else might.',
];

/** The controls, as they are actually bound. Checked against src/main.js. */
export const CONTROLS = [
  { do: 'Walk', key: 'arrows or wasd', touch: 'the stick -- push it any way, including corners' },
  { do: 'Hit with what is in hand', key: 'z or space', touch: 'the big button, named for the weapon' },
  { do: 'Throw the sickle', key: 'z, once the gauge is full', touch: 'the same button' },
  { do: 'Use what is picked: a charm, or eat sap, or press ten glim into one more vigor', key: 'x', touch: 'the one big button -- it says what it will do' },   // THE SECOND ROUND BUTTON WAS DELETED WITH LS-374 (2026-09-18): there is one action button and it names its own verb
  { do: 'Whet a worn weapon: three glim, and the edge is back', key: 'x, with it picked', touch: 'in the bag, tap the picture of what is in your hand' },   // the whet moved to the bag's hand box with LS-374; the action button swings a weapon
  { do: 'Call the ebb: the water goes out for a minute', key: 'x, holding the silt lung, on a shore', touch: 'the one big button -- it says ebb' },
  { do: 'Wear a piece of gear; what was worn there comes into the picked slot', key: 'x, with it picked', touch: 'the one big button -- it says wear' },
  { do: 'In the bag: take off what is worn', key: 'x on an empty slot', touch: 'tap the worn piece' },
  { do: 'Dodge -- a short dash, and the first hit in the half-second after is heavy', key: 'v or shift', touch: 'dodge' },
  { do: 'Pick a hotbar slot', key: '1 to 9', touch: 'tap the slot at the top' },
  { do: 'The next slot', key: 'c, or q', touch: 'next' },
  { do: 'Open the bag', key: 'e', touch: 'bag' },
  { do: 'In the bag: move, lift or place, drop, close', key: 'arrows, z, x, e', touch: 'tap a slot; drop and close are boxes' },
  { do: 'Map', key: 'm', touch: 'map' },
  { do: 'Start, retry, continue', key: 'enter', touch: 'tap the picture' },   // ...WHICH IS WHAT A TITLE, DEATH OR WIN SCREEN ANSWERS (2026-09-18): nothing on the glass is named 'start'
  { do: 'A different world', key: 'r', touch: 'new world' },
  { do: 'Silence', key: 'shift-m', touch: 'sound' },
  { do: 'Change the picture filter', key: 'f', touch: 'filter, in the runs panel' },
];

/** The three charms. `tool` matches TOOLS in src/game.js. */
export const CHARMS = [
  { tool: 'pods', name: 'the rattle pod', label: 'pod',
    what: 'Sets down a rattle pod. It has a short fuse and a loud opinion.',
    more: 'The blast throws whatever is beside it, and that includes you. '
      + 'Stand close on purpose and it will pitch you three tiles: over a lake '
      + 'you have no lung for, over a hedge you cannot burn. It costs a heart '
      + 'and it will drop you somewhere bad if you aim it badly.',
    also: 'Press again with a pod already down and it goes off now, instead '
      + 'of putting a second one beside it. That is how you aim the throw — '
      + 'set it, stand where you want to end up, and press.' },
  { tool: 'tide', name: 'the silt lung', label: 'dive',
    what: 'Takes you under the water and lets you back up.',
    more: 'Under the surface you are slower and you cannot swing, but nothing '
      + 'above can reach you either. A lake is a road and a hiding place, and '
      + 'the same button that takes you down brings you back.',
    also: 'The lung holds what it last breathed. On dry land the same press '
      + 'sprays that lungful: it puts a fire out — the only way to undo one '
      + 'you set badly — and shoves what it hits. One lungful, and going '
      + 'under is how you take another. Standing on a shore the press calls the '
      + 'EBB instead: the water goes out for a minute, a basin bares its bed, and '
      + 'you can walk where you were swimming.' },
  { tool: 'ember', name: 'the emberwake', label: 'burn',
    what: 'Sets an ember on the ground. Fire spreads from it through anything '
      + 'that will take a flame.',
    more: 'The fire is not yours once it is lit. It creeps along a hedge, '
      + 'takes the next one, lights any brazier it passes and sets off any pod '
      + 'it reaches. A thrown sickle carries it too. Think about which way the '
      + 'hedge runs before you put it down.',
    also: 'Standing in water it only hisses, and tells you so — that costs '
      + 'nothing, so the answer is free to find out. Held to a brazier it '
      + 'lights it there and then, rather than being put on the floor and '
      + 'left to find its own way. Carried, it doubles every blow you land: fire in the hand is worth two of anything else.' },
];

/** The bestiary. `kind` matches ENEMY_DEFS in src/entities.ts. The three with `boss` are the quest. */
export const BESTIARY = [
  { kind: 'sporeling', name: 'sporeling', art: 'sporeling_0',
    note: 'Hops, and spits a cloud when it lands. A spore in the blood is a poison; '
      + 'where it breaks the ground keeps spores for a while, and standing in them slows you.' },
  { kind: 'tidecrab', name: 'tidecrab', art: 'tidecrab_0',
    note: 'A crab of the shore, out on the sand at the ebb and gone at the flood. Shelled, slow, and it nips. '
      + 'A shore at low water holds things a shore at high water does not; wait for the tide, or cross it.' },
  { kind: 'mossback', name: 'mossback', art: 'mossback_0',
    note: 'A slug the length of a hand, green with a pale stripe, that goes to wet ground and dies in the dry. '
      + 'Soft, slow, afraid of fire. Scorch the ground and it is gone in seconds; the fen and the shore are where it lives.' },
  { kind: 'sunbasker', name: 'sunbasker', art: 'sunbasker_0',
    note: 'A lizard of the dust and the scree, out by day and gone into the rocks at dusk. '
      + 'Quick where the ground is hot enough to scorch you -- half again as fast -- and slow anywhere cool. Fight it in the shade, or at night not at all.' },
  { kind: 'screehopper', name: 'screehopper', art: 'screehopper_0',
    note: 'A shelled hopper of the scree and the ash that springs a tile at a time and fears no fire. '
      + 'A torch that keeps everything else off does nothing to it; it is a blow that answers it.' },
  { kind: 'stormgull', name: 'stormgull', art: 'stormgull_0',
    note: 'A gull of the coasts that flies only while the rain falls, over water and thicket alike. '
      + 'A torch barely turns it; when the rain stops it is gone. Two blows.' },
  { kind: 'duskling', name: 'duskling', art: 'duskling_0',
    note: 'A small round thing of the bloom and the fen, out only at dusk, the hour the sun goes. '
      + 'It fears fire and hides in the nearest thicket; one blow, if you can catch it before the dark.' },
  { kind: 'gloam', name: 'gloam', art: 'gloam_0',
    note: 'A night moth. Out after dusk, and it goes to any light: a brazier, the torch in your hand. '
      + 'In the dark it is nothing to you; land it on a lit torch and its dust puts the torch out. '
      + 'It burns, so a brazier is the way to be rid of a cloud of them.' },
  { kind: 'cinder', name: 'cinder', art: 'cinder_0',
    note: 'A beetle of the ash and the scree with embers under its shell. Fire does not hurt it: it '
      + 'walks into one and eats it, and every fire it eats is a life more on its back. A fire you set '
      + 'to clear a hedge is a meal to it, and a ring of fire is a lure.' },
  { kind: 'eel', name: 'silt eel', art: 'eel_0',
    note: 'Lives in the water and only the water, deep or shallow, and never sets a fin on land. '
      + 'While you are dry it is a ripple; wade, and it comes. The tide coming in over the '
      + 'shallows you are crossing is what brings it to you.' },
  { kind: 'bramble', name: 'bramble', art: 'bramble_0',
    note: 'Walks straight at you and does not stop. Slow enough to out-walk, '
      + 'patient enough to catch you against a wall. It burns.' },
  { kind: 'wisp', name: 'wisp', art: 'wisp_0',
    note: 'Drifts through walls. A wall is not cover and a corner is not safe.' },
  { kind: 'shellback', name: 'shellback', art: 'shellback_0',
    note: 'Turns an armoured plate toward whatever it is looking at. Hit it '
      + 'from anywhere else.' },
  { kind: 'charger', name: 'charger', art: 'charger_0',
    note: 'Sets its feet, tells you it is coming, and comes. The telegraph is '
      + 'the whole fight -- step aside, not backwards.' },
  { kind: 'sow', name: 'the sow', art: 'charger_0', boss: true,
    note: 'A boar four times the weight, with a boar\'s habits: it winds up, it charges, and '
      + 'when it crashes into rock it lies stunned twice as long as a boar would, and a blow '
      + 'from the axe then counts double. It lies against what it struck: come at it from the open side. Its charge opens a wound that bleeds until you eat sap. It keeps to '
      + 'the ' + BEAST_HOME.sow.join(' and the ') + ' when the land has room there; the stones say where it lies this time. It leaves its hide, worn on the body: a bite does not bleed through it.' },
  { kind: 'oldshell', name: 'the old shell', art: 'shellback_0', boss: true,
    note: 'More life than anything else here, and more each time another beast falls, behind a shell that blocks every blow to its face. Slow. Get behind it, '
      + 'or reach past its guard with the spear. It rears -- earth flying at its feet -- and slams, a ring of force that throws whoever stands within three tiles; wounded to half, it slams twice. It keeps to the ' + BEAST_HOME.oldshell.join(' and the ') + ' when the land has room there; the stones say where it lies this time. It leaves its plate, worn on the body: the first blow in six seconds rings off it.' },
  { kind: 'bramblemother', name: 'the bramble mother', art: 'bramble_0', boss: true,
    note: 'A walking hedge. It catches fire the way a hedge does -- a torch strike, or a fire '
      + 'it walks through -- and while it burns it loses life and every blow counts double. She shudders, and thorns erupt in a line toward you: what you stand in cuts, the rest stays a hedge, so burn it. At half her life two brambles rise beside her. '
      + 'She keeps to the ' + BEAST_HOME.bramblemother.join(' and the ') + ' when the land has room there; the stones say where she lies this time. She leaves her mantle, worn on the body: smoke, spores and fire pass it by.' },
];

/** WHAT IS WORN, GENERATED FROM THE GAME'S OWN TABLE (2026-09-18).
 *
 *  This was five hand-written entries and a lede that stated the count as a fact of the world -- "each beast leaves
 *  what it was made of, and two more pieces lie in caches" -- while the game had grown to thirty-two worn pieces.
 *  Twenty-seven of them, every charm added since, were named nowhere in the booklet a player is handed. So the page
 *  is built from `ITEMS` the way the recipe rows are built from `RECIPES`: every piece with a `gear` slot is on it,
 *  in slot order, and each carries the sentence the game itself uses for it. The five that had a longer hand-written
 *  line -- where it comes from, what it costs -- keep it. */
const SAID: Record<string, string> = {
  hide: 'What the sow leaves. A bite does not bleed through it, and cold water at night does not chill.',
  plate: 'What the old shell leaves. The first blow in six seconds rings off it; thirty turns and it is dented through.',
  mantle: 'What the bramble mother leaves. Smoke, spores and fire pass the wearer by.',
  treads: 'In a cache a few screens out. A sixth faster on land, and no drag in the shallows.',
  bell: 'In a cache further out. Glim falls a tenth more often while it is worn.',
};
const SLOT_ORDER = ['body', 'feet', 'charm'];
const sentence = (note: string) => note.charAt(0).toUpperCase() + note.slice(1) + '.';
export const GEAR = Object.values(ITEMS)
  .filter((it: any) => it && it.gear && it.gear.slot)
  .map((it: any) => ({ kind: it.kind, name: it.name, slot: it.gear.slot, what: SAID[it.kind] || sentence(it.gear.note) }))
  .sort((a, b) => (SLOT_ORDER.indexOf(a.slot) - SLOT_ORDER.indexOf(b.slot)) || a.name.localeCompare(b.name));

/** How to read the ground. `tile` names match T in src/art.js. */
export const LEGEND = [
  { tile: 'GROUND', name: 'Ground', note: 'Walk here.' },
  { tile: 'MOSS', name: 'Moss', note: 'Also walk here. Softer underfoot.' },
  { tile: 'SAND', name: 'Sand', note: 'A path something else wore first.' },
  { tile: 'SHALLOW', name: 'Shallows', note: 'Water you can stand in — with the Lung.' },
  { tile: 'WATER', name: 'Deep water', note: 'The Silt Lung, or go round.' },
  { tile: 'TREE', name: 'Wood', note: 'Solid. The treeline is not straight; look for the gaps.' },
  { tile: 'ROCK', name: 'Stone', note: 'Solid, and it does not burn.' },
  { tile: 'RUBBLE', name: 'Rubble', note: 'A pod opens it. Debris on the ground nearby says so.' },
  { tile: 'THORN', name: 'Thorn', note: 'An ember opens it. A scorch mark nearby says so.' },
  { tile: 'BRAZIER', name: 'Brazier', note: 'Unlit. Fire finds its own way here.' },
  { tile: 'NEST', name: 'Spore nest', note: 'Sends one more thing out every few seconds. Cut it.' },
];

export const NIGHT = [
  'Every seed has another half. End it with !night in the seed field -- '
  + 'glen-wisp-942!night -- for the same world after dark.',
  'Everything in the dark takes an extra hit, and so do you. A torch is a light '
  + 'and a weapon; cold water at night chills you slow.',
  'It is the same world, so what you learn in the dark is true when you go '
  + 'back in daylight. It keeps its own save.',
  'The ground has a temperature. The ash runs hot and the dust by day; the scree and the fen are cold, and every '
  + 'country is colder at night, in the rain and beside water, and warmer beside a fire. Stand where the HUD says '
  + 'COLD and you are chilled (a hide or a lit torch keeps it off); where it says HOT you are scorched, a point every '
  + 'six seconds until you step into water or eat sap (the bramble mantle keeps it off). Frost lies where it stays cold.',
];

/* MAKING THINGS (2026-09-12, LS-384). The recipes are read off the one table the game uses, so the page cannot drift
 * from the fire. The words around them are the rules of the place: no bench, no menu. */
export const MAKING = [
  'A lit brazier with a rock beside it is a forge. Face the rock with a material picked and press the action '
  + 'button: it is laid on the rock, not planted. The rock sparks while anything lies on it, more as the pile nears a whole recipe.',
  'Three times a second the fire reads the rock. The first recipe the pile covers is made, most particular first, '
  + 'and the thing made lies beside the rock. What is laid on the rock is the fire\'s until then.',
  'Two forges stand ready-made: one near the glade with a raft half laid, one on the sow\'s island with a boat half laid. '
  + 'The rock tells you how close it is.',
];
export const RECIPE_ROWS = RECIPES.map((r) => ({
  made: ITEMS[r.out].name,
  from: Object.entries(r.needs).map(([k, n]) => (n > 1 ? n + ' ' : 'a ') + ITEMS[k].name + (n > 1 && !ITEMS[k].name.endsWith('s') ? 's' : '')).join(', '),
}));
